import { useState } from 'react';
import { Container, Database, Droplets, Package } from 'lucide-react';
import { computeAssetSummary } from './assets/mockAssetData';
import IsoTanksGrid from './assets/IsoTanksGrid';
import FlexiTanksGrid from './assets/FlexiTanksGrid';
import StorageTanksGrid from './assets/StorageTanksGrid';

const CATEGORY_TABS = [
  {
    id: 'iso',
    label: 'ISO Tanks',
    sublabel: 'Container assets · Chemical switching',
    icon: Droplets,
    countKey: 'isoCount',
  },
  {
    id: 'flexi',
    label: 'Flexi Tanks',
    sublabel: 'Single-use · Disposal tracking',
    icon: Package,
    countKey: 'flexiCount',
  },
  {
    id: 'storage',
    label: 'Storage Tanks',
    sublabel: 'Depot · Refinery bulk',
    icon: Database,
    countKey: 'storageCount',
  },
];

export default function AssetManagement() {
  const [activeTab, setActiveTab] = useState('iso');
  const summary = computeAssetSummary();

  return (
    <section aria-label="Asset management" className="space-y-5">
      <div className="flex flex-wrap items-end justify-between gap-3 border-b border-logi-grid pb-4">
        <div>
          <div className="mb-2 flex items-center gap-2">
            <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-logi-accent/10 ring-1 ring-logi-accent/30">
              <Container className="h-4 w-4 text-logi-accent" strokeWidth={1.75} />
            </div>
            <span className="text-xs font-semibold uppercase tracking-widest text-logi-accent/80">
              OGTS LogiCore™ · Physical Asset Tracking
            </span>
          </div>
          <h2 className="text-xl font-bold tracking-tight text-logi-text sm:text-2xl">
            Asset Management
          </h2>
          <p className="mt-1 text-sm text-logi-muted">
            ISO containers · Flexi-bag disposables · Depot &amp; refinery bulk storage
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <div className="rounded-xl border border-logi-grid bg-white px-3 py-2 text-center">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-logi-text0">
              Total Assets
            </p>
            <p className="font-mono text-lg font-bold text-logi-text">{summary.totalAssets}</p>
          </div>
          <div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-center">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-logi-text0">
              ISO Alerts
            </p>
            <p className="font-mono text-lg font-bold text-amber-700">
              {summary.isoCriticalClean + summary.isoInspectionDue}
            </p>
          </div>
          <div className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-center">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-logi-text0">
              Storage Critical
            </p>
            <p className="font-mono text-lg font-bold text-red-700">{summary.storageCritical}</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
        <div className="rounded-lg border border-violet-500/20 bg-logi-accent/10 p-2.5">
          <p className="text-[9px] font-bold uppercase tracking-wide text-logi-accent/80">ISO Tanks</p>
          <p className="font-mono text-xl font-bold text-logi-text">{summary.isoCount}</p>
          <p className="text-[9px] text-logi-text0">{summary.isoInspectionDue} inspection due</p>
        </div>
        <div className="rounded-lg border border-amber-500/20 bg-amber-50 p-2.5">
          <p className="text-[9px] font-bold uppercase tracking-wide text-amber-700/80">Clean Overdue</p>
          <p className="font-mono text-xl font-bold text-amber-700">{summary.isoCriticalClean}</p>
          <p className="text-[9px] text-logi-text0">Chemical switch risk</p>
        </div>
        <div className="rounded-lg border border-blue-500/20 bg-logi-accent/100/5 p-2.5">
          <p className="text-[9px] font-bold uppercase tracking-wide text-blue-700/80">Flexi Tanks</p>
          <p className="font-mono text-xl font-bold text-logi-text">{summary.flexiCount}</p>
          <p className="text-[9px] text-logi-text0">{summary.flexiPending} pending disposal</p>
        </div>
        <div className="rounded-lg border border-emerald-500/20 bg-emerald-50 p-2.5">
          <p className="text-[9px] font-bold uppercase tracking-wide text-emerald-700/80">Storage Tanks</p>
          <p className="font-mono text-xl font-bold text-logi-text">{summary.storageCount}</p>
          <p className="text-[9px] text-logi-text0">Depot &amp; refinery sites</p>
        </div>
        <div className="rounded-lg border border-red-500/20 bg-red-50 p-2.5">
          <p className="text-[9px] font-bold uppercase tracking-wide text-red-700/80">Health Alerts</p>
          <p className="font-mono text-xl font-bold text-red-700">{summary.storageCritical}</p>
          <p className="text-[9px] text-logi-text0">Critical / maintenance</p>
        </div>
        <div className="rounded-lg border border-logi-grid bg-white p-2.5">
          <p className="text-[9px] font-bold uppercase tracking-wide text-logi-muted">Categories</p>
          <p className="font-mono text-xl font-bold text-logi-text">3</p>
          <p className="text-[9px] text-logi-text0">ISO · Flexi · Storage</p>
        </div>
      </div>

      <div className="flex gap-1 rounded-xl border border-logi-grid bg-white p-1">
        {CATEGORY_TABS.map((tab) => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;
          const count = summary[tab.countKey];

          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={`flex flex-1 items-center gap-2.5 rounded-lg px-3 py-2.5 text-left transition-all duration-200 ${
                isActive
                  ? 'bg-logi-accent/10 text-logi-accent ring-1 ring-logi-accent/30'
                  : 'text-logi-muted hover:bg-logi-silver/80 hover:text-logi-text'
              }`}
            >
              <Icon
                className={`h-4 w-4 flex-shrink-0 ${isActive ? 'text-logi-accent' : 'text-logi-text0'}`}
                strokeWidth={1.75}
              />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <p className="truncate text-sm font-semibold">{tab.label}</p>
                  <span className="font-mono text-[10px] text-logi-text0">{count}</span>
                </div>
                <p className="truncate text-[10px] text-logi-text0">{tab.sublabel}</p>
              </div>
            </button>
          );
        })}
      </div>

      <div key={activeTab} className="animate-[fadeSlideIn_0.25s_ease-out]">
        {activeTab === 'iso' && <IsoTanksGrid />}
        {activeTab === 'flexi' && <FlexiTanksGrid />}
        {activeTab === 'storage' && <StorageTanksGrid />}
      </div>
    </section>
  );
}
